import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { NavLink, useLocation } from 'react-router-dom';
import { useHistory } from 'react-router';
import i18next from 'i18next';
import PropTypes from 'prop-types';

import { useSelector } from 'react-redux';
import Account, { AccountDropdown, AccountIconWrapper } from './Account';
import AccountAvatar, {
  AccountAvatarCircle,
  avatarActiveBorderColor,
  headerMobileBreakpoint,
  isCompactAccountHeader,
} from './AccountAvatar';
import NavigationDropdown from './NavigationDropdown';
import InsiderPlusSupportDropdown from './InsiderPlusSupportDropdown';
import { shouldShowInsiderPlusSupportDropdown } from '../../util/helpers/insiderPlusSupportHelper';

import {
  EMAIL_LOGIN_PAGE,
  IDENTITY_PAGE,
  RESET_PASSWORD,
  RESET_SECURITY_QUESTION_PAGE,
  RESET_SENT_PAGE,
  RESET_SUCCESSFUL_PAGE,
  SECURITY_QUESTION_PAGE,
  USER_LOGIN_PAGE,
  IMPERSONATE_PAGE,
  INVITE_USER_PAGE,
  NOT_FOUND_PAGE,
  ERROR_PAGE,
  MAINTENANCE_PAGE,
  TWO_FA_VERIFY_IDENTITY,
  TWO_FA_VERIFY_IDENTITY_PHONE,
  TWO_FA_QR_CODE_PAGE,
  VERIFY_CHOOSE_METHOD_PAGE,
  TWO_FA_CHOOSE_PHONE_METHOD,
  TWO_FA_VERIFY_CODE,
  TWO_FA_SUCCESSFUL_SETUP,
  TWO_FA_HAVING_TROUBLE_PAGE,
  TWO_FA_FORGOT_PASSWORD_IDENTITY_PAGE,
  TWO_FA_FORGOT_PASSWORD_PAGE,
  TWO_FA_FORGOT_PASSWORD_IDENTITY_PHONE_PAGE,
  TWO_FA_FORGOT_PASSWORD_IDENTITY_HAVING_TROUBLE_PAGE,
  // QUICK_REGISTRATION_PAGE,
  TWO_STEP_REGISTRATION_PAGE,
  TERMS,
  USER_TERMS,
  ACH_TERMS,
  IRA_TERMS,
  INSIDER_PLUS_MEMBERSHIP_PAGE,
  INSIDER_PLUS_PAGE,
  PORTFOLIO_PAGE,
} from '../../constants/pages';
import {
  accountNavItems,
  closedAccountNavItems,
  getInsiderPlusNavItems,
} from '../../constants/header';

import {
  selectSettings,
  selectIsInsiderPlusEnabledForUser,
} from '../../store/selectors/settingsSelectors';
import useWindowSize from '../../util/hooks/useIsMobileHook';
import { buttonizeDiv } from '../../util/buttonizeDiv';
import Anchor from '../Anchor/Anchor';
import SupportModal from '../Modals/SupportModal/SupportModal';
import GTTFlow from '../GTT/GTTFlow';
import TriggerOrderBlockModal from '../GTT/TriggerOrderBlockModal';
import BuyFlow from '../Buy/BuyFlow';
import IraDepositWizard from '../Modals/DepositWizard/IraDepositWizard';
import MilleniumDepositWizard from '../Modals/DepositWizard/MilleniumDepositWizard';
import { useIsClosedAccount } from '../../util/hooks/useIsClosedAccount';
import useCheckPermissions from '../../util/hooks/useCheckPermissions';
import { IRA_ENTRUST_TYPE, IRA_EQUITY_TYPE } from '../../util/constants';
import {
  retrieveFromSessionStorage,
  storeInSessionStorage,
} from '../../util/helpers/sessionStorageHelper';
import {
  IS_FUND_MODAL_OPEN,
  IS_HEADER_BUY_FOR_STORAGE_OPEN,
  IS_TRIGGER_ORDER_OPEN,
  PREVENT_SUPPORT_MODAL,
  TRIGGER_ORDER_WIZARD,
  WIZARD_DATA,
} from '../../constants/sessionStorage';
import { useSessionStorageState } from '../../util/hooks/useSessionStorageState';
import { setWizardContent } from '../../util/helpers/wizardHelpers';
import useGtmHook from '../../util/hooks/useGtmHook';

import {
  mediaBelow,
  mediaBetween,
  pxToRem,
  pxToRemMd,
} from '../../assets/styles/helper';
import { variables } from '../../assets/styles/variables';
import themeColors from '../../assets/styles/themeColors';
import {
  uButtonClear,
  uFlexCenter,
  uOutlineNone,
  uFlexColumn,
} from '../../assets/styles/utility';
import config from '../../config';
import Logo from '../Logo/Logo';
import StandardDepositWizard from '../Modals/Deposit/StandardDepositWizard/StandardDepositWizard';
import { getCurrentAccount } from '../../store/selectors/accountSelectors';
import useTriggerOrderVerification from '../../util/hooks/useTriggerOrderVerification';
import useTargetPriceAgain from '../../util/hooks/useTargetPriceAgain';
import {
  selectHasInsiderPlusSubscription,
  selectHasInsiderPlusMembershipHistory,
  selectCanManageInsiderPlusMembership,
  selectInsiderPlusCurrentCourseId,
  selectPriorityInsiderPlusSupportPhone,
} from '../../store/selectors/userSelectors';
import { ENTRY_POINTS } from '../GTT/Common/gtmEventHelper';
import {
  CloseIcon,
  HamburgerIcon,
  UserIcon,
} from '../Common/Icons/HeaderIcons';
import { useCrmEvent } from '../../util/helpers/crmEventsHelper';
import {
  EVENT_TYPES_ENUM,
  FUND_ORDER_STARTED_TYPE,
} from '../../util/enum/api/crmEvents';

const hideNavigationRoutes = [
  EMAIL_LOGIN_PAGE,
  USER_LOGIN_PAGE,
  RESET_PASSWORD,
  SECURITY_QUESTION_PAGE,
  IDENTITY_PAGE,
  RESET_SUCCESSFUL_PAGE,
  RESET_SENT_PAGE,
  RESET_SECURITY_QUESTION_PAGE,
  IMPERSONATE_PAGE,
  INVITE_USER_PAGE,
  NOT_FOUND_PAGE,
  ERROR_PAGE,
  TERMS,
  USER_TERMS,
  ACH_TERMS,
  IRA_TERMS,
  MAINTENANCE_PAGE,
  VERIFY_CHOOSE_METHOD_PAGE,
  TWO_FA_QR_CODE_PAGE,
  TWO_FA_CHOOSE_PHONE_METHOD,
  TWO_FA_VERIFY_CODE,
  TWO_FA_SUCCESSFUL_SETUP,
  TWO_FA_VERIFY_IDENTITY,
  TWO_FA_VERIFY_IDENTITY_PHONE,
  TWO_FA_HAVING_TROUBLE_PAGE,
  TWO_FA_FORGOT_PASSWORD_IDENTITY_PAGE,
  TWO_FA_FORGOT_PASSWORD_PAGE,
  TWO_FA_FORGOT_PASSWORD_IDENTITY_PHONE_PAGE,
  TWO_FA_FORGOT_PASSWORD_IDENTITY_HAVING_TROUBLE_PAGE,
  // QUICK_REGISTRATION_PAGE,
  TWO_STEP_REGISTRATION_PAGE,
];

const headerHeightDesktop = pxToRem(80);
const headerHeightMobile = pxToRemMd(74);
// The app steps its --legacy-scale down to 0.875 below bpXxl (1350px, base.js).
// The GoldSilver header does it from here, so the Insider+ nav still fits
// beside the logo.
const HEADER_SCALE_BREAKPOINT = 1430;
const { brokerTheme, metalStreamCaption } = config;
const Hamburger = HamburgerIcon(brokerTheme);
const User = UserIcon(brokerTheme);
const Close = CloseIcon(brokerTheme);
const getHeaderBackground = () => {
  switch (brokerTheme) {
    case 'HardAssetsAlliance':
      return themeColors.colorHeaderLogoDark;

    case 'GoldSilver':
      return `${themeColors.colorMainHeaderBackground}`;
    case 'Contoso':
      return;
    case 'LegacyPM':
      return;

    default:
      return themeColors.colorHeaderLogoDark;
  }
};

const isContoso = () => {
  const { brokerTheme } = config;
  if (brokerTheme === 'Contoso') {
    return true;
  }
  return false;
};

const HeaderContainer = styled.header`
  display: flex;
  padding-left: calc(3.25rem * var(--legacy-scale, 1));
  align-items: center;
  position: sticky;
  top: 0;
  right: 0;
  left: 0;
  z-index: ${variables.zIndex.indexXl};

  ${({ showBackground, isGS }) =>
    showBackground && isGS
      ? `background-color: ${getHeaderBackground()};`
      : `background-image: url(${getHeaderBackground()});`}

  height: ${headerHeightDesktop};
  border: none;
  box-shadow: ${variables.shadow.boxShadow};
  ${({ showBackground, isGS }) =>
    showBackground && !isGS && `background-color: ${getHeaderBackground()};`}

  ${isCompactAccountHeader &&
  `${mediaBetween(variables.breakpoints.bpXxl, HEADER_SCALE_BREAKPOINT)} {
      --legacy-scale: 0.875;
      font-size: calc(1rem * var(--legacy-scale, 1));
    }`}

  ${mediaBelow(headerMobileBreakpoint)} {
    ${({ showBackground, isGS }) =>
      showBackground && isGS
        ? `background-color: ${getHeaderBackground()};`
        : `background-image: url(${getHeaderBackground()});`}
    ${({ showBackground, isGS }) =>
      showBackground && !isGS && `background-color: ${getHeaderBackground()};`}
    padding-left: 0;
    padding: 0 ${pxToRemMd(24)};
    box-shadow: ${variables.shadow.boxShadow};
    height: ${headerHeightMobile};
  }
`;

const HeaderHamburgerButton = styled.button`
  ${uButtonClear};
  ${uOutlineNone};
  cursor: pointer;
  display: none;

  svg {
    width: ${pxToRem(36)};
    height: ${pxToRem(36)};
  }

  ${mediaBelow(headerMobileBreakpoint)} {
    display: flex;
  }
`;

const HeaderAccountButton = styled.button`
  ${uButtonClear};
  ${uOutlineNone};
  cursor: pointer;
  display: none;

  > svg {
    width: ${pxToRem(36)};
    height: ${pxToRem(36)};
  }

  ${mediaBelow(headerMobileBreakpoint)} {
    display: flex;
  }
`;

const HeaderLogo = styled.div`
  margin-right: ${pxToRem(48)};

  ${({ logoWhite }) =>
    logoWhite &&
    `
      svg {
        color: ${themeColors.colorHeaderLogoLight};
      }
    `}

  ${({ logoDark }) =>
    logoDark &&
    `
      svg {
        color: ${themeColors.colorHeaderLogoDark};
      }
    `}  
  ${({ isDesktopShow }) =>
    isDesktopShow &&
    `display: flex;

  `};
  ${({ isMobileShow }) =>
    isMobileShow &&
    `display: none;
  `};

  img,
  svg {
    width: ${isContoso() ? pxToRem(300) : pxToRem(200)};
    object-fit: contain;
  }

  ${mediaBelow(headerMobileBreakpoint)} {
    margin: 0 auto;
    ${(props) =>
      props.isDesktopShow &&
      `display: none;
    `};
    ${(props) =>
      props.isMobileShow &&
      `display: flex;
  
    `};

    img,
    svg {
      width: ${pxToRemMd(200)};
      object-fit: contain;
    }
  }
`;

const HeaderNavigation = styled.nav`
  display: flex;
  align-self: stretch;
  justify-content: flex-end;
  max-width: ${pxToRem(734)};
  width: 100%;
  margin-left: auto;
  margin-right: ${isCompactAccountHeader ? 0 : pxToRem(72)};

  ${mediaBelow(headerMobileBreakpoint)} {
    position: fixed;
    top: ${headerHeightMobile};
    left: 0;
    right: 0;
    bottom: 0;
    justify-content: flex-start;
    flex-direction: column;
    margin-right: 0;
    background-color: ${themeColors.colorSecondaryHeaderBackground};
    z-index: ${variables.zIndex.indexSm};
    display: none;
    max-width: 100%;
    overflow: auto;
    ${(props) => props.isNavOpen && `display: flex`};
  }
`;

// GoldSilver: never narrower than its items. With Insider+ the items outgrow the
// nav's max-width, and a shrunken list spills them out past the nav's right
// edge, under the account avatar. Kept at full width, the nav's flex-end
// alignment makes any overflow run left instead.
const HeaderNavigationList = styled.ul`
  display: flex;
  ${isCompactAccountHeader && `flex-shrink: 0;`}
  padding-right: ${pxToRem(12)};

  ${mediaBelow(headerMobileBreakpoint)} {
    flex-direction: column;
    ${isCompactAccountHeader && `flex-shrink: 1;`}
  }
`;

const HeaderNavigationItem = styled.li`
  ${uFlexCenter};
  min-width: ${pxToRem(64)};
  font-size: ${pxToRem(16)};
  text-decoration: none;
  flex-shrink: 0;

  &:not(:first-child) {
    margin-left: ${pxToRem(22)};
  }

  > a {
    text-decoration: none;
    line-height: 1.5;
    color: ${themeColors.colorTextContrast};
    text-transform: uppercase;
    font-size: ${pxToRem(18)};

    &.active {
      color: ${themeColors.colorBrandLight};
    }
  }

  > button {
    ${uOutlineNone};
    background-color: transparent;
    align-self: stretch;
    width: 100%;
    border: 0;
    line-height: 1.5;
    font-size: ${pxToRem(18)};
    color: ${themeColors.colorTextContrast};
    text-transform: uppercase;
    cursor: pointer;
  }

  ${mediaBelow(headerMobileBreakpoint)} {
    margin: 0;
    width: 100%;
    justify-content: flex-start;

    &:hover {
      background-color: ${themeColors.colorNavigationItemHover};
    }

    &:not(:first-child) {
      margin-left: 0;
    }

    > a {
      color: ${themeColors.colorTextPrimary};
      padding: ${pxToRemMd(12)} ${pxToRemMd(16)};
      font-size: ${pxToRemMd(18)};
      line-height: 1.35;
      width: 100%;
      text-transform: capitalize;

      &.active {
        color: ${themeColors.colorBrandLight};
      }
    }

    > button {
      align-self: flex-start;
      display: flex;
      color: ${themeColors.colorTextPrimary};
      padding: ${pxToRemMd(12)} ${pxToRemMd(16)};
      font-size: ${pxToRemMd(18)};
      line-height: 1.35;
      text-transform: capitalize;

      &:hover {
        background-color: ${themeColors.colorNavigationItemHover};
      }
  }
`;

// What shows the account menu is open: the avatar's ring, or the chevron flipping.
const accountOpenCss = isCompactAccountHeader
  ? `${AccountAvatarCircle} {
      border-color: ${avatarActiveBorderColor};
    }`
  : `${AccountIconWrapper} svg {
      transform: rotate(180deg);
    }`;

const HeaderAccountWrap = styled.div`
  ${isCompactAccountHeader
    ? `flex-shrink: 0;`
    : `
  max-width: ${pxToRem(400)};
  width: 100%;`}
  align-self: stretch;
  ${isCompactAccountHeader &&
  `&:hover ${AccountAvatarCircle} {
    border-color: ${avatarActiveBorderColor};
  }`}

  ${(props) =>
    props.isDropdownOpen &&
    `
    ${AccountDropdown} {
      ${uFlexColumn};
    }

    ${accountOpenCss}
  `}

  ${mediaBelow(headerMobileBreakpoint)} {
    position: fixed;
    z-index: ${variables.zIndex.indexSm};
    top: ${headerHeightMobile};
    left: 0;
    right: 0;
    bottom: 0;
    width: 100%;
    max-width: 100%;
    display: none;
    overflow: auto;
    background-color: ${themeColors.colorSecondaryHeaderBackground};
    ${(props) => props.isAccountOpen && `display: flex`};
  }
`;

const Header = ({ maintenance, setNoPermissionsModal }) => {
  const { pathname, hash } = useLocation();
  const history = useHistory();
  const windowSize = useWindowSize();
  // The Header's CSS (here and in the other Header components) switches to its
  // mobile layout at the same width.
  const isMobile = windowSize.width < headerMobileBreakpoint;
  const [isNavOpen, setIsNavOpen] = useState(isMobile || false);
  const [isAccountOpen, setIsAccountOpen] = useState(false);
  const [isSupportModalOpen, setSupportModalOpen] = useState(false);
  // Persist the GTT modal's open state so a page refresh reopens it on the same
  // step with the same values (mirrors how BuyFlow retains its modal via
  // useSessionStorageState + setWizardContent). The step (TRIGGER_ORDER_WIZARD)
  // and WIZARD_DATA already persist on their own.
  const [isGttModalOpen, setGttModalOpenState] = useSessionStorageState(
    IS_TRIGGER_ORDER_OPEN,
    false,
  );
  const setGttModalOpen = (value) => {
    setWizardContent(IS_TRIGGER_ORDER_OPEN, value);
    setGttModalOpenState(value);
  };
  // Persist the Buy modal opened via the GTT->Buy hand-off so a refresh reopens
  // it (its WIZARD_DATA is already seeded by handleSwitchToBuyFlow).
  const [
    isBuyForStorageOpen,
    setIsBuyForStorageOpenState,
  ] = useSessionStorageState(IS_HEADER_BUY_FOR_STORAGE_OPEN, false);
  const setIsBuyForStorageOpen = (value) => {
    setWizardContent(IS_HEADER_BUY_FOR_STORAGE_OPEN, value);
    setIsBuyForStorageOpenState(value);
  };
  const {
    allowAccountChangesPermission,
    displayIRAEntrustOptionsPermission,
    displayIRAMilleniumOptionsPermission,
    displayMetalStreamMenuItems,
    displayIRAEquityOptionsPermission,
    displayStandardOptionsPermission,
  } = useCheckPermissions();
  const [isModalOpen, setIsModalOpen] = useSessionStorageState(
    IS_FUND_MODAL_OPEN,
    false,
  );
  const { gtmDialogScreenView } = useGtmHook();
  const locationHistory = history.location.pathname;
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const { displayBasicAccountOptionsPermission } = useCheckPermissions();
  const locationHistoryWithoutParam =
    locationHistory.lastIndexOf('/') === 0
      ? locationHistory
      : locationHistory.substr(0, locationHistory.lastIndexOf('/'));

  const [showNavigation, setShowNavigation] = useState(
    () => !hideNavigationRoutes.includes(locationHistoryWithoutParam),
  );
  const settings = useSelector(selectSettings);
  const isClosedAccount = useIsClosedAccount();
  const currentAccount = useSelector(getCurrentAccount);
  const {
    isTriggerOrderEntryHidden,
    verificationVariant,
    closeVerification,
    blockIfIneligible,
  } = useTriggerOrderVerification();
  // "Set target price again" on an expired order opens the Target Price flow
  // from wherever the order's details were open (Portfolio, Financial Balances,
  // a product page), for its product and vault; this is the flow's host on
  // every page.
  useTargetPriceAgain({
    isEnabled: !!settings?.IsTriggerOrderActive && !isTriggerOrderEntryHidden,
    blockIfIneligible,
    openFlow: () => setGttModalOpen(true),
  });
  const hasInsiderPlusSubscription = useSelector(
    selectHasInsiderPlusSubscription,
  );
  const isInsiderPlusEnabledForUser = useSelector(
    selectIsInsiderPlusEnabledForUser,
  );
  const hasInsiderPlusMembershipHistory = useSelector(
    selectHasInsiderPlusMembershipHistory,
  );
  const canManageInsiderPlusMembership = useSelector(
    selectCanManageInsiderPlusMembership,
  );
  const insiderPlusCurrentCourseId = useSelector(
    selectInsiderPlusCurrentCourseId,
  );
  const priorityInsiderPlusSupportPhone = useSelector(
    selectPriorityInsiderPlusSupportPhone,
  );
  const insiderPlusNavItems = useMemo(
    () => getInsiderPlusNavItems(insiderPlusCurrentCourseId),
    [insiderPlusCurrentCourseId],
  );
  const hasInsiderPlus =
    hasInsiderPlusSubscription && isInsiderPlusEnabledForUser;
  // [Insider+] Only members with the priority-support benefit get the support
  // dropdown; everyone else keeps the regular Support button.
  const showInsiderPlusSupportDropdown = shouldShowInsiderPlusSupportDropdown({
    hasInsiderPlus,
    phone: priorityInsiderPlusSupportPhone,
  });
  // [Insider+] The Membership management entry is gated on membership history
  // — not on an active subscription — so freemium users (active subscription,
  // no history) don't see it — and on the backend's CanManageMembership flag.
  // Anyone who has held a membership and is allowed to manage it, active or
  // lapsed, keeps it so they can view/renew. No per-user override: test
  // accounts follow the same rule. The nav link remains gated on an active
  // subscription.
  const hasInsiderPlusMembership =
    !!hasInsiderPlusMembershipHistory &&
    canManageInsiderPlusMembership &&
    isInsiderPlusEnabledForUser;
  const isGS = brokerTheme === 'GoldSilver';
  const isErrAccount = currentAccount?.AccountStatus === 7;
  const isPendingAccount =
    currentAccount?.AccountStatus === 4 || currentAccount?.AccountStatus === 1;

  const shouldShowMetalStreamLink = () => displayMetalStreamMenuItems && !isGS;

  const handleCrmEvent = useCrmEvent();

  useEffect(() => {
    setShowNavigation(
      !hideNavigationRoutes.includes(locationHistoryWithoutParam),
    );
  }, [history, history.location, locationHistoryWithoutParam]);

  const openNavigation = () => {
    setIsNavOpen(!isNavOpen);

    if (isAccountOpen) {
      setIsAccountOpen(false);
    }
  };

  const accountToggleIcon = isCompactAccountHeader ? (
    <AccountAvatar account={currentAccount} />
  ) : (
    <User />
  );

  const openAccount = () => {
    setIsAccountOpen(!isAccountOpen);

    if (isNavOpen) {
      setIsNavOpen(false);
    }
  };

  const openSupport = () => {
    setSupportModalOpen(true);

    if (isNavOpen) {
      setIsNavOpen(false);
    }
  };

  // "Buy at Current Price?" hand-off from the GTT order-entry screen. Close the
  // GTT modal via the Header handler (not GTTFlow's internal closeModal, which
  // wipes WIZARD_DATA), then seed the shared WIZARD_DATA in the Buy-flow shape
  // AFTER closing so the seed survives, and open the regular Buy-for-storage
  // flow pre-populated with the same product, vault and quantity. Stashes the
  // GTT journey's entry point under its own gttEntryPoint key (not Buy's own
  // entryPoint, which has a separate vocabulary and drives Buy's own GTM
  // events — see PriceAlerts/Common/gtmEventHelper's ENTRY_POINTS), so a later
  // "Buy at Target Price?" switch back to GTT can still report the journey's
  // real origin instead of a generic default, without Buy's events picking up
  // a GTT-only value.
  const handleSwitchToBuyFlow = (product, vault, quantity) => {
    setGttModalOpen(false);
    const existingWizardData = retrieveFromSessionStorage(WIZARD_DATA) || {};
    storeInSessionStorage(WIZARD_DATA, {
      storageProduct: product,
      selectedVault: vault,
      orderValues: { QuantityAmount: quantity, CashAmount: 0 },
      activeType: 'quantity',
      ...(existingWizardData.entryPoint != null && {
        gttEntryPoint: existingWizardData.entryPoint,
      }),
    });
    setIsBuyForStorageOpen(true);
  };

  // "Buy at Target Price?" hand-off from the Buy order-selection screen: close
  // the Buy flow, seed the GTT-flow shape into WIZARD_DATA, land on the GTT
  // order-entry step (index 1) so the product carries over, then reopen the GTT
  // modal. Mirror of handleSwitchToBuyFlow. Preserves the GTT journey's own
  // gttEntryPoint (stashed by handleSwitchToBuyFlow above on a prior round
  // trip) — not Buy's entryPoint, which may hold a Buy-vocabulary value that
  // isn't a valid GTT entry point — falling back to TOP_NAV_BANNER only when
  // there's no prior GTT entry point to restore.
  const handleSwitchToTargetPrice = (product, vault, quantity) => {
    // Same permission gate as the Buy page's Buy at Target Price CTA — this
    // link is another entry point into GTT, so an ineligible user could
    // otherwise bypass the main CTA's restriction by switching over from inside
    // the Buy flow.
    if (blockIfIneligible()) {
      return;
    }
    setIsBuyForStorageOpen(false);
    const existingWizardData = retrieveFromSessionStorage(WIZARD_DATA) || {};
    storeInSessionStorage(WIZARD_DATA, {
      entryPoint:
        existingWizardData.gttEntryPoint ?? ENTRY_POINTS.TOP_NAV_BANNER,
      selectedProduct: product,
      ...(vault && vault.LocationType !== undefined
        ? { selectedVault: vault }
        : {}),
      ...(quantity ? { orderValues: { QuantityAmount: quantity } } : {}),
    });
    setWizardContent(TRIGGER_ORDER_WIZARD, 1);
    setGttModalOpen(true);
  };

  const closeNavigation = () => {
    setIsNavOpen(false);
  };

  useEffect(() => {
    setIsNavOpen(false);
    setIsAccountOpen(false);
  }, [pathname, hash]);

  useEffect(() => {
    if (isDropdownOpen) {
      setIsDropdownOpen(false);
    }

    if (isNavOpen) {
      setIsNavOpen(false);
    }

    if (isAccountOpen) {
      setIsAccountOpen(false);
    }
  }, [isMobile]); // eslint-disable-line

  useEffect(() => {
    if (isMobile) {
      if (isNavOpen || isAccountOpen) {
        document.body.style.overflow = 'hidden';
      } else {
        document.body.style.overflow = 'auto';
      }
    }
  }, [isNavOpen, isAccountOpen, isMobile]);

  const logoAnchor = () => {
    if (settings.WebsiteUrl) {
      if (String(settings.WebsiteUrl).includes('https://')) {
        return settings.WebsiteUrl;
      }

      return `https://${settings.WebsiteUrl}`;
    }
  };

  const mobileLogo = () => (
    <Anchor type="website" value={logoAnchor()} text={<Logo />} />
  );
  const getAccountNavItems = () => {
    if (isClosedAccount) {
      return closedAccountNavItems;
    }

    if (!hasInsiderPlusMembership) {
      // [Insider+] Hide the Membership entry unless the user has Insider+
      // membership history and CanManageMembership is true. Lapsed (expired)
      // members who can manage keep it so they can view/renew; freemium users
      // (no history) and users who can't manage don't see it.
      return accountNavItems.filter(
        (item) => item.href !== INSIDER_PLUS_MEMBERSHIP_PAGE,
      );
    }

    return accountNavItems;
  };

  const insiderPlusLabel = i18next.t('header.linkInsiderPlus');

  if (maintenance || !showNavigation) {
    if (
      // locationHistory === QUICK_REGISTRATION_PAGE ||
      locationHistory === TWO_STEP_REGISTRATION_PAGE
    ) {
      return null;
    }

    return (
      <HeaderContainer
        showBackground={!isContoso()}
        isGS={config.brokerTheme === 'GoldSilver'}
      >
        <HeaderLogo logoWhite isDesktopShow>
          <Anchor type="website" value={logoAnchor()} text={<Logo />} />
        </HeaderLogo>
        <HeaderLogo logoDark isMobileShow>
          {mobileLogo()}
        </HeaderLogo>
      </HeaderContainer>
    );
  }

  return (
    <>
      <HeaderContainer
        showBackground={!isContoso()}
        isGS={config.brokerTheme === 'GoldSilver'}
      >
        <HeaderHamburgerButton
          onClick={() => openNavigation()}
          data-cy="button-nav-list-toggle-mobile"
        >
          {!isNavOpen ? <Hamburger /> : <Close />}
        </HeaderHamburgerButton>

        <HeaderLogo logoWhite isDesktopShow>
          <Anchor type="website" value={logoAnchor()} text={<Logo />} />
        </HeaderLogo>
        <HeaderLogo logoDark isMobileShow>
          {mobileLogo()}
        </HeaderLogo>

        <HeaderNavigation isNavOpen={isNavOpen}>
          <HeaderNavigationList>
            {/* [Insider+] Subscribers get a dropdown into the insider-plus
                modules (its title still links to /investor-hub on desktop);
                everyone else gets a plain link to /investor-hub, which shows
                the join flow. hasInsiderPlusSubscription is null while it
                loads — wait for it, so the item doesn't switch from link to
                dropdown after first paint. */}
            {!isClosedAccount &&
              isInsiderPlusEnabledForUser &&
              hasInsiderPlusSubscription !== null && (
                <HeaderNavigationItem>
                  {hasInsiderPlus ? (
                    <NavigationDropdown
                      isNavOpen={isNavOpen}
                      title={insiderPlusLabel}
                      titleHref={INSIDER_PLUS_PAGE}
                      items={insiderPlusNavItems}
                      closeNav={closeNavigation}
                      isMobile={isMobile}
                      isWideList
                      startCollapsedOnMobile
                      dataCyLabel="nav-insider-plus"
                    />
                  ) : (
                    <NavLink
                      onClick={closeNavigation}
                      to={INSIDER_PLUS_PAGE}
                      data-cy="nav-insider-plus"
                    >
                      {insiderPlusLabel}
                    </NavLink>
                  )}
                </HeaderNavigationItem>
              )}
            {!isClosedAccount && (
              <HeaderNavigationItem>
                <NavLink
                  onClick={closeNavigation}
                  to="/portfolio"
                  // Portfolio owns /portfolio and /portfolio/product/* only.
                  // /portfolio/financial-balances is an Account-menu page, so
                  // exclude it here — otherwise the default prefix match would
                  // light up Portfolio on the Account dropdown's page.
                  isActive={(_, { pathname }) =>
                    pathname === PORTFOLIO_PAGE ||
                    pathname.startsWith(`${PORTFOLIO_PAGE}/product`)
                  }
                  data-cy="nav-portfolio"
                >
                  {i18next.t('header.portfolioLink')}
                </NavLink>
              </HeaderNavigationItem>
            )}
            {allowAccountChangesPermission &&
              !displayBasicAccountOptionsPermission && (
                <HeaderNavigationItem>
                  <button
                    onClick={() => {
                      storeInSessionStorage(PREVENT_SUPPORT_MODAL, true);
                      setWizardContent(IS_FUND_MODAL_OPEN, true);
                      setIsModalOpen(true);
                      gtmDialogScreenView({
                        title: 'Deposit Funds',
                      });
                      handleCrmEvent(
                        currentAccount,
                        EVENT_TYPES_ENUM.Fund_OrderStarted,
                        {
                          Source: FUND_ORDER_STARTED_TYPE.MainNavigation,
                        },
                      );
                    }}
                    data-cy="nav-fund"
                  >
                    {i18next.t('header.fundBtn')}
                  </button>
                </HeaderNavigationItem>
              )}
            {!isClosedAccount && (
              <>
                <HeaderNavigationItem>
                  <NavLink
                    onClick={closeNavigation}
                    to="/buy"
                    data-cy="nav-buy"
                  >
                    {i18next.t('header.linkBuy')}
                  </NavLink>
                </HeaderNavigationItem>

                <HeaderNavigationItem>
                  <NavLink
                    onClick={closeNavigation}
                    to="/sell"
                    data-cy="nav-sell"
                  >
                    {i18next.t('header.linkSell')}
                  </NavLink>
                </HeaderNavigationItem>

                {shouldShowMetalStreamLink() && (
                  <HeaderNavigationItem>
                    <NavLink
                      onClick={closeNavigation}
                      to="/aip"
                      data-cy="nav-metalstream"
                    >
                      {i18next.t('header.linkMetalStream', {
                        metalStream: metalStreamCaption,
                      })}
                    </NavLink>
                  </HeaderNavigationItem>
                )}
              </>
            )}

            <HeaderNavigationItem>
              <NavigationDropdown
                isNavOpen={isNavOpen}
                title={i18next.t('header.navDropdownAccount')}
                items={getAccountNavItems()}
                closeNav={closeNavigation}
                isMobile={isMobile}
                dataCyLabel="nav-account"
              />
            </HeaderNavigationItem>
            <HeaderNavigationItem>
              {showInsiderPlusSupportDropdown ? (
                <InsiderPlusSupportDropdown
                  phone={priorityInsiderPlusSupportPhone}
                  email={settings.SupportEmail}
                  isNavOpen={isNavOpen}
                  isMobile={isMobile}
                />
              ) : (
                <button onClick={() => openSupport()} data-cy="nav-support">
                  {i18next.t('header.supportBtn')}
                </button>
              )}
            </HeaderNavigationItem>
          </HeaderNavigationList>
        </HeaderNavigation>
        <HeaderAccountButton
          onClick={() => openAccount()}
          data-cy="button-account-list-toggle-mobile"
        >
          {!isAccountOpen ? accountToggleIcon : <Close />}
        </HeaderAccountButton>
        <HeaderAccountWrap
          isAccountOpen={isAccountOpen}
          isDropdownOpen={isDropdownOpen}
          {...buttonizeDiv(() => {
            setIsDropdownOpen(!isDropdownOpen);
          })}
          data-cy="button-account-list-toggle"
        >
          <Account
            setIsAccountOpen={setIsAccountOpen}
            setIsDropdownOpen={setIsDropdownOpen}
            setNoPermissionsModal={setNoPermissionsModal}
            isBasicAccount={
              displayBasicAccountOptionsPermission &&
              !isErrAccount &&
              !isPendingAccount
            }
          />
        </HeaderAccountWrap>

        <SupportModal
          handleOutsideClick
          isOpen={isSupportModalOpen}
          close={setSupportModalOpen}
        />
        {isGttModalOpen && (
          <GTTFlow
            isModalOpen={isGttModalOpen}
            handleCloseModal={() => setGttModalOpen(false)}
            onSwitchToBuyFlow={handleSwitchToBuyFlow}
          />
        )}
        <TriggerOrderBlockModal
          variant={verificationVariant}
          close={closeVerification}
        />
        {isBuyForStorageOpen && (
          <BuyFlow
            isModalOpen={isBuyForStorageOpen}
            handleCloseModal={() => setIsBuyForStorageOpen(false)}
            onSwitchToTargetPrice={
              settings?.IsTriggerOrderActive && !isTriggerOrderEntryHidden
                ? handleSwitchToTargetPrice
                : undefined
            }
          />
        )}
      </HeaderContainer>
      {displayStandardOptionsPermission && isModalOpen && (
        <StandardDepositWizard
          isModalOpen={isModalOpen}
          handleClose={() => setIsModalOpen(false)}
        />
      )}
      {displayIRAEquityOptionsPermission && isModalOpen && (
        <IraDepositWizard
          isModalOpen={isModalOpen}
          handleClose={() => setIsModalOpen(false)}
          type={IRA_EQUITY_TYPE}
        />
      )}
      {displayIRAEntrustOptionsPermission && isModalOpen && (
        <IraDepositWizard
          isModalOpen={isModalOpen}
          handleClose={() => setIsModalOpen(false)}
          type={IRA_ENTRUST_TYPE}
        />
      )}
      {displayIRAMilleniumOptionsPermission && isModalOpen && (
        <MilleniumDepositWizard
          isModalOpen={isModalOpen}
          handleClose={() => setIsModalOpen(false)}
        />
      )}
    </>
  );
};

Header.propTypes = {
  maintenance: PropTypes.bool,
  setNoPermissionsModal: PropTypes.func,
};

export default Header;
